/* eslint no-param-reassign: ["error", { "props": false }] */
/* eslint no-undef: ["error", { "typeof": true }] */
import { TimelineMax, TweenMax } from 'gsap';
import checkZero from '@/scripts/utils/lead-zero';
import imagesLoaded from 'imagesloaded/imagesloaded.pkgd.min';
import Hammer from 'hammerjs';
import normalizeWheel from 'normalize-wheel';
import detectEditMode from '@/scripts/utils/detect-edit-mode';

class CoverSlider {
  constructor(options) {
    if (detectEditMode()) {
      return;
    }
    this.slider = document.querySelector(options.slider);
    this.slideSelector = options.slideSelector;
    this.slides = this.slider.querySelectorAll(this.slideSelector);
    this.slideImages = this.slider.querySelectorAll(`${this.slideSelector} img`);
    this.slideLength = this.slides.length;
    this.navNext = this.slider.querySelector(options.navNextSelector);
    this.navPrev = this.slider.querySelector(options.navPrevSelector);
    this.paginationContainer = this.slider.querySelector(options.paginationSelector);
    this.paginationBullets = [];
    this.textElements = [];
    this.currentSlideIndex = 0;
    this.animationDuration = 1000;
    this.working = false;
    this.scrolling = false;
    this.tl = new TimelineMax();
    this.body = document.querySelector('body');
    this.header = document.querySelector('header');
    this.init();
  }

  initPagination(index) {
    const slideNumber = checkZero(index);
    const numberOne = slideNumber[0];
    const numberTwo = slideNumber[1];
    const bullet = `<span class="line"></span><span class="number"><strong>${numberOne}</strong><strong>${numberTwo}</strong></span>`;
    const el = document.createElement('div');
    el.classList.add('pagination-bullet');
    el.innerHTML = bullet;
    this.paginationContainer.appendChild(el);
  }

  changePaginationBullet() {
    this.paginationContainer.querySelector('.active').classList.remove('active');
    this.paginationBullets[this.currentSlideIndex].classList.add('active');
  }

  hideNavigation() {
    TweenMax.to(this.paginationContainer, 0.5, { autoAlpha: 0, y: -60 });
    TweenMax.to(this.navNext, 0.5, { autoAlpha: 0, y: 60 });
    TweenMax.to(this.navPrev, 0.5, { autoAlpha: 0, y: 60 });
  }

  showNavigation() {
    TweenMax.to(this.paginationContainer, 0.5, { autoAlpha: 1, y: 0 });
    TweenMax.to(this.navNext, 0.5, { autoAlpha: 1, y: 0 });
    TweenMax.to(this.navPrev, 0.5, { autoAlpha: 1, y: 0 });
  }

  changeSlide(currentIndex) {
    const that = this;
    this.working = true;
    this.changePaginationBullet();
    let leavePercent = '-100%';
    if (currentIndex > this.currentSlideIndex) {
      leavePercent = '100%';
    }

    this.slides[this.currentSlideIndex].classList.add('active');
    this.slides[currentIndex].classList.remove('active');

    if (this.slides[this.currentSlideIndex].dataset.scrollbar) {
      this.hideNavigation();
      this.header.classList.add('is-scroll');
    } else {
      this.showNavigation();
      this.header.classList.remove('is-scroll');
    }

    TweenMax.set(this.slides[currentIndex], { zIndex: 1 });
    TweenMax.set(this.slides[this.currentSlideIndex], { zIndex: 2 });

    TweenMax.to(this.slides[currentIndex], 1.2, { y: leavePercent, overwrite: 5 });
    this.tl
      .to(this.slides[this.currentSlideIndex], 0.6, { y: '0%', overwrite: 5 })
      .to(this.slideImages[this.currentSlideIndex], 1.5, { scale: 1 });
    TweenMax.staggerTo(this.textElements[this.currentSlideIndex], 0.7, { autoAlpha: 1, delay: 0.5 }, 0.15);
    this.tl
      .set(this.textElements[currentIndex], { autoAlpha: 0 })
      .set(this.slideImages[currentIndex], {
        scale: 1.3,
        onComplete: () => {
          that.working = false;
        },
      });
  }

  navigateDown() {
    if (this.currentSlideIndex === this.slideLength - 1) return;
    this.currentSlideIndex += 1;
    this.changeSlide(this.currentSlideIndex - 1);
  }

  navigationUp() {
    if (this.currentSlideIndex === 0) return;
    this.currentSlideIndex -= 1;
    this.changeSlide(this.currentSlideIndex + 1);
  }

  initSlider() {
    Array.from(this.slides).forEach((el, i) => {
      this.initPagination(i + 1);
      const childBlocks = el.querySelectorAll('[data-cover-animation="child"]');
      const child = [];
      Array.from(childBlocks).map(block => Array.from(block.children).map(childEl => child.push(childEl)));

      this.textElements.push(child);

      if (i !== 0) {
        TweenMax.set(el, { y: '100%', zIndex: 2, overwrite: 5 });
        if (this.textElements[i].length) {
          TweenMax.set(this.textElements[i], { autoAlpha: 0 });
          TweenMax.set(this.slideImages[i], { scale: 1.3 });
        }
      } else {
        TweenMax.set(el, { y: '0%', zIndex: 1, overwrite: 5 });
        el.classList.add('active');
      }
    });
    this.paginationBullets = this.paginationContainer.children;
    this.paginationBullets[this.currentSlideIndex].classList.add('active');
  }

  init() {
    const that = this;
    this.body.style.overflow = 'hidden';
    this.initSlider();

    const scroller = this.slider.querySelector('.cover-slider__item_scroller');
    scroller.addEventListener('touchmove', e => e.stopPropagation());

    Array.from(this.slides).forEach((el) => {
      el.addEventListener('wheel', (e) => {
        const normalized = normalizeWheel(e);
        const { pixelY } = normalized;
        if (that.working) return;
        if (pixelY < -10) {
          that.navigationUp();
        } else if (pixelY > 10) {
          that.navigateDown();
        }
      });

      const manager = new Hammer.Manager(el);
      manager.add(new Hammer.Pan({
        direction: Hammer.DIRECTION_VERTICAL,
      }));
      const threshold = el.contains(scroller) ? 20 : 10;
      manager.on('pan', (e) => {
        if (!that.working && Math.abs(e.deltaY) > threshold) {
          if (e.deltaY > 0) {
            that.navigationUp();
          }
          if (e.deltaY < 0) {
            that.navigateDown();
          }
        }
      });
    });

    this.navNext.addEventListener('click', () => {
      if (that.working) return;
      that.navigateDown();
    });
    this.navPrev.addEventListener('click', () => {
      if (that.working) return;
      that.navigationUp();
    });
  }
}

export default function (options) {
  if (!$(options.slider).length) return;
  const imgLoad = imagesLoaded(options.slider);
  imgLoad.on('done', () => {
    new CoverSlider(options);
  });
}




// WEBPACK FOOTER //
// ./scripts/blocks/cover-slider.js